import { createGlobalStyle } from 'styled-components'
import { switchProp, ifProp } from 'styled-tools'
import { eweFonts } from '../fonts'
import { basePixelSize } from './baseTheme'

const gutterWidth = '15px'

const GlobalStyles = createGlobalStyle`
  ${eweFonts};
  :root {
    font: 400 ${basePixelSize}px/1.43 "Tahoma", sans-serif;
  }
  body {
    padding: 0;
    margin: 0;
    font-family: "Tahoma", Arial, Helvetica, sans-serif;
  }
  header {
    font-family: "Graphik", sans-serif; 
  }
  footer nav ul li a,
  footer div p {
    font-family: "Graphik", sans-serif;
  }
`
const colors = {
  red: '#E40033',
  black: '#000',
  darkGrey: '#424242',
  grey: '#E1DEDD',
  white: '#FFF',
}

const contextualColors = {
  primary: colors.darkGrey,
  secondary: colors.darkGrey,
  link: colors.red,
  backgroundPrimary: colors.white,
  backgroundSecondary: colors.grey,
  lightboxBackground: colors.white,
  overlayText: colors.darkGrey,
  overlayButton: colors.red,
}

const baseTypography = {
  fontFamily: '"Graphik", sans-serif',
  titleFontFamily: '"Tahoma", Arial, Helvetica, sans-serif',
  inputFontFamily: '"Tahoma", Arial, Helvetica, sans-serif',
}

const typography = {
  imagePageTitle: {
    fontFamily: baseTypography.fontFamily,
    fontWeight: 700,
    fontSize: ifProp('small', '16px', '22px'),
    lineHeight: ifProp('small', 1.18, 1.18),
    fontSizeDesktop: ifProp('small', '50px', '63px'),
    lineHeightDesktop: ifProp('small', 1, 1.12),
    marginTop: 0,
    marginBottom: '30px',
    textTransform: 'uppercase',
    textShadow: '0 0 30px rgba(0, 0, 0, 0.43)',
    letterSpacing: '2px',
  },
  pageTitle: {
    fontFamily: baseTypography.titleFontFamily,
    fontWeight: 200,
    fontSize: ifProp('small', '40px', '48px'),
    fontStyle: 'normal',
    lineHeight: ifProp('small', '44px', '52px'),
    marginTop: 0,
    marginBottom: '30px',
    textTransform: 'none',
    letterSpacing: 0,
    color: colors.darkGrey,
  },
  headline: {
    fontFamily: baseTypography.titleFontFamily,
    fontWeight: 400,
    fontSize: '24px',
    fontStyle: 'normal',
    lineHeight: 1.375,
    fontSizeDesktop: '30px',
    lineHeightDesktop: 1.375,
    marginTop: '10px',
    marginBottom: '10px',
    textTransform: 'normal',
    letterSpacing: 0,
    color: colors.darkGrey,
  },
  subHeadline: {
    fontFamily: baseTypography.titleFontFamily,
    fontWeight: 400,
    fontSize: '20px',
    fontStyle: 'normal',
    lineHeight: 1.375,
    fontSizeDesktop: '20px',
    lineHeightDesktop: 1.375,
    marginTop: '10px',
    marginBottom: '10px',
    textTransform: 'normal',
    letterSpacing: 0,
    color: colors.darkGrey,
  },
  title: {
    fontFamily: baseTypography.titleFontFamily,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: 1.428,
    fontSizeDesktop: '16px',
    lineHeightDesktop: 1.428,
    marginTop: 0,
    marginBottom: '30px',
    textTransform: 'none',
    letterSpacing: 0,
  },
  preamble: {
    fontFamily: baseTypography.fontFamily,
    fontWeight: 400,
    fontSize: '15px',
    lineHeight: '22px',
    fontSizeDesktop: '18px',
    lineHeightDesktop: 1.44,
    marginTop: 0,
    marginBottom: '30px',
    textTransform: 'none',
    letterSpacing: 0,
    color: colors.darkGrey,
  },
  paragraph: {
    fontFamily: baseTypography.fontFamily,
    fontWeight: 400,
    fontSize: switchProp('size', {
      small: '14px',
      large: '18px',
      default: '16px',
    }),
    lineHeight: switchProp('size', {
      small: 1.5,
      large: 1.375,
      default: 1.428,
    }),
    fontSizeDesktop: switchProp('size', {
      small: '14px',
      large: '18px',
      default: '16px',
    }),
    lineHeightDesktop: switchProp('size', {
      small: 1.5,
      large: 1.375,
      default: 1.428,
    }),
    marginTop: 0,
    marginBottom: '30px',
    textTransform: 'none',
    letterSpacing: 0,
    color: colors.darkGrey,
  },
  link: {
    color: contextualColors.link,
    textDecoration: 'underline',
  },
}

const tooltip = {
  colors: {
    primaryColor: colors.white,
    primaryBackground: colors.darkGrey,
  },
  paragraph: {
    fontFamily: '"Graphik", sans-serif',
  },
}

const forms = {
  colors: {
    selected: colors.red,
  },
}

const button = {
  borderRadius: 0,
  borderWidth: '0 0 1px 0',
  borderLeftPosition: '15%',
  borderRightPosition: '15%',
  fontFamily: baseTypography.fontFamily,
  fontSize: '13px',
  fontWeight: '400',
  textTransform: 'uppercase',
  letterSpacing: '1.5px',
  colors: {
    primaryColor: colors.white,
    primaryBackground: colors.red,
    secondaryColor: colors.white,
    secondaryBackground: colors.darkGrey,
    successColor: colors.white,
    successBackground: colors.red,
  },
  margin: '0',
  padding: switchProp('size', {
    small: ifProp({ kind: 'inverted' }, `10px 0px`, `10px 40px`),
    normal: ifProp({ kind: 'inverted' }, `10px 0px`, `15px 45px`),
    large: ifProp({ kind: 'inverted' }, `10px 0px`, `20px 50px`),
  }),
  siblingMargin: gutterWidth,
}

const theme = {
  basePixelSize,
  GlobalStyles,
  typography,
  tooltip,
  button,
  forms,
  colors: {
    ...colors,
    ...contextualColors,
  },
}

export default theme
